import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Container,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
    Badge,
    Table,
    Row,
    Col,
    Modal,
    Tabs,
    Tab,
    InputGroup,
} from 'react-bootstrap';
import {
    FaPlay,
    FaCog,
    FaCheckCircle,
    FaTimesCircle,
    FaEdit,
    FaTrash,
    FaChartLine,
    FaArrowDown,
    FaArrowUp,
    FaShieldAlt,
    FaBalanceScale,
    FaExclamationTriangle,
    FaPlus,
    FaSyncAlt,
} from 'react-icons/fa';

const API_URL = 'http://localhost:8001/api';

interface Instance {
    id: number;
    name: string;
    status: string;
    broker: {
        id: number;
        name: string;
    };
    broker_account: {
        id: number;
        account_id: string;
    };
    linked_algos: Array<{
        id: number;
        name: string;
    }>;
}

interface Position {
    id: string;
    symbol: string;
    direction: string;
    quantity: number;
    entry_price: number;
    current_price: number;
    stop_loss_price: number | null;
    take_profit_price: number | null;
    unrealized_pnl: number;
    realized_pnl: number;
    status: string;
    algo_id: number;
    algo_name: string;
    broker_account_id: number;
    created_at: string | null;
    exit_reason: string | null;
    exit_price: number | null;
}

interface TestResult {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
}

const TestTradeManagement: React.FC = () => {
    // State for instances and positions
    const [instances, setInstances] = useState<Instance[]>([]);
    const [positions, setPositions] = useState<Position[]>([]);
    const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);

    // Loading states
    const [loading, setLoading] = useState(false);
    const [loadingPositions, setLoadingPositions] = useState(false);

    // Result modal
    const [showResultModal, setShowResultModal] = useState(false);
    const [result, setResult] = useState<TestResult | null>(null);

    // Create Position Modal
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newPosition, setNewPosition] = useState({
        instance_id: 0,
        linked_algo_id: 0,
        symbol: '',
        direction: 'LONG',
        entry_price: '',
        quantity: '',
        stop_loss_price: '',
        take_profit_price: ''
    });

    // Test Modals
    const [activeTab, setActiveTab] = useState('positions');
    const [testParams, setTestParams] = useState({
        // SL/TP Adjust
        new_stop_loss: '',
        new_take_profit: '',
        // Simulate Price
        simulated_price: '',
        // Trailing Stop
        trail_type: 'Percentage',
        trail_amount: '1',
        trail_price: '',
        // Breakeven
        breakeven_threshold: '',
        breakeven_price: '',
        // Drawdown
        drawdown_threshold: '',
        drawdown_price: '',
        // Close
        close_reason: 'MANUAL',
        close_price: ''
    });

    // Load instances on mount
    useEffect(() => {
        loadInstances();
        loadPositions();
    }, []);

    const loadInstances = async () => {
        try {
            const response = await axios.get(`${API_URL}/test-trade-management/instances`);
            setInstances(response.data.instances || []);
        } catch (error: any) {
            console.error('Error loading instances:', error);
        }
    };

    const loadPositions = useCallback(async (instanceId?: number) => {
        try {
            setLoadingPositions(true);
            const url = instanceId
                ? `${API_URL}/test-trade-management/positions?instance_id=${instanceId}`
                : `${API_URL}/test-trade-management/positions`;
            const response = await axios.get(url);
            setPositions(response.data.positions || []);
        } catch (error: any) {
            console.error('Error loading positions:', error);
        } finally {
            setLoadingPositions(false);
        }
    }, []);

    // Create Position
    const handleCreatePosition = async () => {
        if (!newPosition.symbol || !newPosition.entry_price || !newPosition.quantity) {
            alert('Please fill in required fields');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/test-trade-management/create-position`, {
                ...newPosition,
                entry_price: parseFloat(newPosition.entry_price),
                quantity: parseFloat(newPosition.quantity),
                stop_loss_price: newPosition.stop_loss_price ? parseFloat(newPosition.stop_loss_price) : undefined,
                take_profit_price: newPosition.take_profit_price ? parseFloat(newPosition.take_profit_price) : undefined
            });
            setResult(response.data);
            setShowResultModal(true);
            setShowCreateModal(false);
            loadPositions(selectedInstance?.id);
            // Reset form
            setNewPosition({
                instance_id: selectedInstance?.id || 0,
                linked_algo_id: 0,
                symbol: '',
                direction: 'LONG',
                entry_price: '',
                quantity: '',
                stop_loss_price: '',
                take_profit_price: ''
            });
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Failed to create position',
                error: error.response?.data?.detail || error.message
            });
            setShowResultModal(true);
        } finally {
            setLoading(false);
        }
    };

    // Adjust SL/TP
    const handleAdjustSLTP = async () => {
        if (!selectedPosition) return;

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/test-trade-management/adjust-sltp`, {
                position_id: selectedPosition.id,
                new_stop_loss: testParams.new_stop_loss ? parseFloat(testParams.new_stop_loss) : undefined,
                new_take_profit: testParams.new_take_profit ? parseFloat(testParams.new_take_profit) : undefined
            });
            setResult(response.data);
            setShowResultModal(true);
            loadPositions(selectedInstance?.id);
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Failed to adjust SL/TP',
                error: error.response?.data?.detail || error.message
            });
            setShowResultModal(true);
        } finally {
            setLoading(false);
        }
    };

    // Simulate Price
    const handleSimulatePrice = async () => {
        if (!selectedPosition || !testParams.simulated_price) return;

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/test-trade-management/simulate-price`, {
                position_id: selectedPosition.id,
                simulated_price: parseFloat(testParams.simulated_price)
            });
            setResult(response.data);
            setShowResultModal(true);
            loadPositions(selectedInstance?.id);
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Failed to simulate price',
                error: error.response?.data?.detail || error.message
            });
            setShowResultModal(true);
        } finally {
            setLoading(false);
        }
    };

    // Test Trailing Stop
    const handleTestTrailingStop = async () => {
        if (!selectedPosition || !testParams.trail_price) return;

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/test-trade-management/test-trailing-stop`, {
                position_id: selectedPosition.id,
                trail_type: testParams.trail_type,
                trail_amount: parseFloat(testParams.trail_amount),
                simulated_price: parseFloat(testParams.trail_price)
            });
            setResult(response.data);
            setShowResultModal(true);
            loadPositions(selectedInstance?.id);
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Failed to test trailing stop',
                error: error.response?.data?.detail || error.message
            });
            setShowResultModal(true);
        } finally {
            setLoading(false);
        }
    };

    // Test Breakeven
    const handleTestBreakeven = async () => {
        if (!selectedPosition || !testParams.breakeven_threshold || !testParams.breakeven_price) return;

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/test-trade-management/test-breakeven`, {
                position_id: selectedPosition.id,
                breakeven_threshold: parseFloat(testParams.breakeven_threshold),
                simulated_price: parseFloat(testParams.breakeven_price)
            });
            setResult(response.data);
            setShowResultModal(true);
            loadPositions(selectedInstance?.id);
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Failed to test breakeven',
                error: error.response?.data?.detail || error.message
            });
            setShowResultModal(true);
        } finally {
            setLoading(false);
        }
    };

    // Test Drawdown Exit
    const handleTestDrawdownExit = async () => {
        if (!selectedPosition || !testParams.drawdown_threshold || !testParams.drawdown_price) return;

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/test-trade-management/test-drawdown-exit`, {
                position_id: selectedPosition.id,
                drawdown_threshold: parseFloat(testParams.drawdown_threshold),
                simulated_price: parseFloat(testParams.drawdown_price)
            });
            setResult(response.data);
            setShowResultModal(true);
            loadPositions(selectedInstance?.id);
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Failed to test drawdown exit',
                error: error.response?.data?.detail || error.message
            });
            setShowResultModal(true);
        } finally {
            setLoading(false);
        }
    };

    // Close Position
    const handleClosePosition = async () => {
        if (!selectedPosition) return;

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/test-trade-management/close-position`, {
                position_id: selectedPosition.id,
                reason: testParams.close_reason,
                exit_price: testParams.close_price ? parseFloat(testParams.close_price) : undefined
            });
            setResult(response.data);
            setShowResultModal(true);
            loadPositions(selectedInstance?.id);
            setSelectedPosition(null);
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Failed to close position',
                error: error.response?.data?.detail || error.message
            });
            setShowResultModal(true);
        } finally {
            setLoading(false);
        }
    };

    // Delete Position
    const handleDeletePosition = async (positionId: string) => {
        if (!window.confirm('Are you sure you want to delete this position?')) return;

        try {
            await axios.delete(`${API_URL}/test-trade-management/position/${positionId}`);
            loadPositions(selectedInstance?.id);
            if (selectedPosition?.id === positionId) {
                setSelectedPosition(null);
            }
        } catch (error: any) {
            alert(`Error deleting position: ${error.response?.data?.detail || error.message}`);
        }
    };

    const handleInstanceChange = (instanceId: number) => {
        const instance = instances.find((i) => i.id === instanceId);
        setSelectedInstance(instance || null);
        setSelectedPosition(null);
        if (instance) {
            loadPositions(instance.id);
            setNewPosition((prev) => ({ ...prev, instance_id: instance.id }));
        }
    };

    const getPnLColor = (pnl: number) => {
        if (pnl > 0) return 'success';
        if (pnl < 0) return 'danger';
        return 'secondary';
    };

    const getStatusBadge = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return <Badge bg="success">ACTIVE</Badge>;
            case 'CLOSED':
                return <Badge bg="secondary">CLOSED</Badge>;
            case 'OPENING':
                return <Badge bg="warning">OPENING</Badge>;
            default:
                return <Badge bg="dark">{status}</Badge>;
        }
    };

    return (
        <Container fluid className="py-4">
            <style>{`
                .trade-management-container {
                    min-height: 100vh;
                    padding: 0;
                }

                .main-card {
                    background: #11131a;
                    border: 1px solid #252a38;
                    border-radius: 8px;
                    overflow: hidden;
                }

                .card-header-custom {
                    background: #181b24;
                    color: #e8eaed;
                    padding: 1.5rem;
                    border-bottom: 1px solid #252a38;
                }

                .section-card {
                    background: #11131a;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-bottom: 1rem;
                    border: 1px solid #252a38;
                }

                .test-panel {
                    background: #181b24;
                    border-radius: 8px;
                    padding: 1.5rem;
                    border: 1px solid #252a38;
                }

                .position-row {
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .position-row:hover {
                    background-color: #1e2230 !important;
                }

                .position-row.selected {
                    background-color: #2a3550 !important;
                    border-left: 2px solid #6b9fff;
                }

                .test-button {
                    padding: 0.6rem 1.25rem;
                    font-weight: 600;
                    font-size: 0.875rem;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                }

                .test-button:hover {
                    transform: translateY(-1px);
                }

                .price-simulation {
                    background: #181b24;
                    color: #e8eaed;
                    border: 1px solid #252a38;
                    border-radius: 8px;
                    padding: 1rem;
                }

                .result-box {
                    background: #090a0f;
                    border: 1px solid #1a1e2a;
                    border-radius: 6px;
                    padding: 1rem;
                    font-family: 'JetBrains Mono', monospace;
                    font-size: 0.8125rem;
                    color: #8b919a;
                    max-height: 300px;
                    overflow-y: auto;
                }

                .nav-tabs .nav-link {
                    color: #8b919a;
                    font-weight: 500;
                    font-size: 0.8125rem;
                }

                .nav-tabs .nav-link.active {
                    background: #181b24;
                    color: #e8eaed;
                    border: 1px solid #252a38;
                    border-bottom-color: #11131a;
                }

                .pnl-positive {
                    color: #4ade80;
                    font-weight: 600;
                }

                .pnl-negative {
                    color: #f87171;
                    font-weight: 600;
                }

                .direction-long {
                    color: #4ade80;
                    font-weight: 600;
                }

                .direction-short {
                    color: #f87171;
                    font-weight: 600;
                }
            `}</style>

            <div className="trade-management-container">
                <Container>
                    <Card className="main-card">
                        <div className="card-header-custom">
                            <h2 className="mb-0 d-flex align-items-center gap-2">
                                <FaShieldAlt size={32} />
                                Test Trade Management
                            </h2>
                            <p className="mb-0 mt-2" style={{ opacity: 0.9 }}>
                                Visually test UTML trade management: SL/TP adjustment, trailing stops, breakeven, drawdown exits
                            </p>
                        </div>

                        <div className="p-4">
                            {/* Instance Selection */}
                            <div className="section-card">
                                <Row className="align-items-center">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <FaCog className="me-2" />
                                                Select Trading Instance
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedInstance?.id || ''}
                                                onChange={(e) => handleInstanceChange(Number(e.target.value))}
                                                size="lg"
                                            >
                                                <option value="">-- All Positions --</option>
                                                {instances.map((instance) => (
                                                    <option key={instance.id} value={instance.id}>
                                                        {instance.name} ({instance.broker?.name})
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} className="text-end">
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                if (selectedInstance) {
                                                    setNewPosition((prev) => ({
                                                        ...prev,
                                                        instance_id: selectedInstance.id
                                                    }));
                                                    setShowCreateModal(true);
                                                } else {
                                                    alert('Please select an instance first');
                                                }
                                            }}
                                            className="me-2"
                                        >
                                            <FaPlus className="me-2" />
                                            Create Test Position
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => loadPositions(selectedInstance?.id)}
                                        >
                                            <FaSyncAlt className="me-2" />
                                            Refresh
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            <Row>
                                {/* Positions Table */}
                                <Col lg={7}>
                                    <div className="section-card">
                                        <h5 className="mb-3">
                                            <FaChartLine className="me-2" />
                                            Positions
                                        </h5>

                                        {loadingPositions ? (
                                            <div className="text-center py-4">
                                                <Spinner animation="border" variant="primary" />
                                            </div>
                                        ) : positions.length === 0 ? (
                                            <Alert variant="info">
                                                No positions found. Create a test position to get started.
                                            </Alert>
                                        ) : (
                                            <Table hover size="sm" responsive>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Symbol</th>
                                                        <th>Dir</th>
                                                        <th>Entry</th>
                                                        <th>Current</th>
                                                        <th>SL</th>
                                                        <th>TP</th>
                                                        <th>P&L</th>
                                                        <th>Status</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {positions.map((pos) => (
                                                        <tr
                                                            key={pos.id}
                                                            className={`position-row ${selectedPosition?.id === pos.id ? 'selected' : ''
                                                                }`}
                                                            onClick={() => {
                                                                setSelectedPosition(pos);
                                                                setTestParams((prev) => ({
                                                                    ...prev,
                                                                    new_stop_loss: pos.stop_loss_price?.toString() || '',
                                                                    new_take_profit: pos.take_profit_price?.toString() || '',
                                                                    simulated_price: pos.current_price?.toString() || ''
                                                                }));
                                                            }}
                                                        >
                                                            <td>
                                                                <strong>{pos.symbol}</strong>
                                                                <br />
                                                                <small className="text-muted">{pos.algo_name}</small>
                                                            </td>
                                                            <td>
                                                                <span className={pos.direction === 'LONG' ? 'direction-long' : 'direction-short'}>
                                                                    {pos.direction === 'LONG' ? <FaArrowUp /> : <FaArrowDown />}
                                                                    {' '}{pos.direction}
                                                                </span>
                                                            </td>
                                                            <td>{pos.entry_price ? `$${pos.entry_price.toFixed(2)}` : 'N/A'}</td>
                                                            <td>{pos.current_price ? `$${pos.current_price.toFixed(2)}` : 'N/A'}</td>
                                                            <td>
                                                                {pos.stop_loss_price ? (
                                                                    <span className="text-danger">${pos.stop_loss_price.toFixed(2)}</span>
                                                                ) : '-'}
                                                            </td>
                                                            <td>
                                                                {pos.take_profit_price ? (
                                                                    <span className="text-success">${pos.take_profit_price.toFixed(2)}</span>
                                                                ) : '-'}
                                                            </td>
                                                            <td>
                                                                <span className={pos.status === 'ACTIVE' ?
                                                                    ((pos.unrealized_pnl ?? 0) >= 0 ? 'pnl-positive' : 'pnl-negative') :
                                                                    ((pos.realized_pnl ?? 0) >= 0 ? 'pnl-positive' : 'pnl-negative')
                                                                }>
                                                                    {(pos.status === 'ACTIVE' ? pos.unrealized_pnl : pos.realized_pnl) !== null &&
                                                                        (pos.status === 'ACTIVE' ? pos.unrealized_pnl : pos.realized_pnl) !== undefined
                                                                        ? `$${(pos.status === 'ACTIVE' ? pos.unrealized_pnl : pos.realized_pnl).toFixed(2)}`
                                                                        : 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>{getStatusBadge(pos.status)}</td>
                                                            <td>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeletePosition(pos.id);
                                                                    }}
                                                                >
                                                                    <FaTrash />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </div>
                                </Col>

                                {/* Test Panel */}
                                <Col lg={5}>
                                    <div className="section-card">
                                        <h5 className="mb-3">
                                            <FaCog className="me-2" />
                                            Trade Management Tests
                                        </h5>

                                        {!selectedPosition ? (
                                            <Alert variant="warning">
                                                <FaExclamationTriangle className="me-2" />
                                                Select a position from the table to run tests
                                            </Alert>
                                        ) : selectedPosition.status !== 'ACTIVE' ? (
                                            <Alert variant="info">
                                                Position is {selectedPosition.status}. Only ACTIVE positions can be tested.
                                            </Alert>
                                        ) : (
                                            <Tabs
                                                activeKey={activeTab}
                                                onSelect={(k) => setActiveTab(k || 'sltp')}
                                                className="mb-3"
                                            >
                                                {/* SL/TP Adjustment Tab */}
                                                <Tab eventKey="sltp" title="SL/TP">
                                                    <div className="test-panel">
                                                        <h6><FaEdit className="me-2" />Adjust Stop Loss / Take Profit</h6>
                                                        <p className="text-muted small">Modify SL/TP prices for the selected position.</p>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>New Stop Loss</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.new_stop_loss}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, new_stop_loss: e.target.value }))}
                                                                    placeholder={selectedPosition.stop_loss_price?.toString() || 'Enter price'}
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>New Take Profit</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.new_take_profit}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, new_take_profit: e.target.value }))}
                                                                    placeholder={selectedPosition.take_profit_price?.toString() || 'Enter price'}
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Button
                                                            variant="primary"
                                                            className="test-button w-100"
                                                            onClick={handleAdjustSLTP}
                                                            disabled={loading}
                                                        >
                                                            {loading ? <Spinner size="sm" /> : 'Adjust SL/TP'}
                                                        </Button>
                                                    </div>
                                                </Tab>

                                                {/* Price Simulation Tab */}
                                                <Tab eventKey="price" title="Price Sim">
                                                    <div className="test-panel">
                                                        <h6><FaChartLine className="me-2" />Simulate Price Movement</h6>
                                                        <p className="text-muted small">Check if SL/TP thresholds would be triggered.</p>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Simulated Price</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.simulated_price}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, simulated_price: e.target.value }))}
                                                                    placeholder="Enter price"
                                                                />
                                                            </InputGroup>
                                                            <Form.Text className="text-muted">
                                                                Current: ${selectedPosition.current_price?.toFixed(2)}
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Button
                                                            variant="info"
                                                            className="test-button w-100"
                                                            onClick={handleSimulatePrice}
                                                            disabled={loading || !testParams.simulated_price}
                                                        >
                                                            {loading ? <Spinner size="sm" /> : 'Simulate Price'}
                                                        </Button>
                                                    </div>
                                                </Tab>

                                                {/* Trailing Stop Tab */}
                                                <Tab eventKey="trail" title="Trail">
                                                    <div className="test-panel">
                                                        <h6><FaArrowUp className="me-2" />Test Trailing Stop</h6>
                                                        <p className="text-muted small">Simulate price movement and trailing stop behavior.</p>

                                                        <Row>
                                                            <Col md={6}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label>Trail Type</Form.Label>
                                                                    <Form.Select
                                                                        value={testParams.trail_type}
                                                                        onChange={(e) => setTestParams((prev) => ({ ...prev, trail_type: e.target.value }))}
                                                                    >
                                                                        <option value="Percentage">Percentage</option>
                                                                        <option value="Fixed Amount">Fixed Amount</option>
                                                                    </Form.Select>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label>Trail Amount</Form.Label>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={testParams.trail_amount}
                                                                        onChange={(e) => setTestParams((prev) => ({ ...prev, trail_amount: e.target.value }))}
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Simulated Price</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.trail_price}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, trail_price: e.target.value }))}
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Button
                                                            variant="warning"
                                                            className="test-button w-100"
                                                            onClick={handleTestTrailingStop}
                                                            disabled={loading || !testParams.trail_price}
                                                        >
                                                            {loading ? <Spinner size="sm" /> : 'Test Trailing Stop'}
                                                        </Button>
                                                    </div>
                                                </Tab>

                                                {/* Breakeven Tab */}
                                                <Tab eventKey="breakeven" title="BE">
                                                    <div className="test-panel">
                                                        <h6><FaBalanceScale className="me-2" />Test Breakeven</h6>
                                                        <p className="text-muted small">Test breakeven activation when P&L exceeds threshold.</p>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Breakeven Threshold ($)</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.breakeven_threshold}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, breakeven_threshold: e.target.value }))}
                                                                    placeholder="P&L threshold"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Simulated Price</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.breakeven_price}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, breakeven_price: e.target.value }))}
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Button
                                                            variant="success"
                                                            className="test-button w-100"
                                                            onClick={handleTestBreakeven}
                                                            disabled={loading || !testParams.breakeven_threshold || !testParams.breakeven_price}
                                                        >
                                                            {loading ? <Spinner size="sm" /> : 'Test Breakeven'}
                                                        </Button>
                                                    </div>
                                                </Tab>

                                                {/* Drawdown Tab */}
                                                <Tab eventKey="drawdown" title="DD Exit">
                                                    <div className="test-panel">
                                                        <h6><FaArrowDown className="me-2" />Test Drawdown Exit</h6>
                                                        <p className="text-muted small">Test position close when drawdown exceeds threshold.</p>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Drawdown Threshold ($)</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.drawdown_threshold}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, drawdown_threshold: e.target.value }))}
                                                                    placeholder="Max loss before close"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Simulated Price</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.drawdown_price}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, drawdown_price: e.target.value }))}
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Button
                                                            variant="danger"
                                                            className="test-button w-100"
                                                            onClick={handleTestDrawdownExit}
                                                            disabled={loading || !testParams.drawdown_threshold || !testParams.drawdown_price}
                                                        >
                                                            {loading ? <Spinner size="sm" /> : 'Test Drawdown Exit'}
                                                        </Button>
                                                    </div>
                                                </Tab>

                                                {/* Close Position Tab */}
                                                <Tab eventKey="close" title="Close">
                                                    <div className="test-panel">
                                                        <h6><FaTimesCircle className="me-2" />Close Position</h6>
                                                        <p className="text-muted small">Manually close the selected position.</p>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Close Reason</Form.Label>
                                                            <Form.Select
                                                                value={testParams.close_reason}
                                                                onChange={(e) => setTestParams((prev) => ({ ...prev, close_reason: e.target.value }))}
                                                            >
                                                                <option value="MANUAL">Manual Close</option>
                                                                <option value="STOP_LOSS">Stop Loss</option>
                                                                <option value="TAKE_PROFIT">Take Profit</option>
                                                                <option value="DRAWDOWN">Drawdown</option>
                                                                <option value="TRAILING_STOP">Trailing Stop</option>
                                                            </Form.Select>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Exit Price (optional)</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text>$</InputGroup.Text>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={testParams.close_price}
                                                                    onChange={(e) => setTestParams((prev) => ({ ...prev, close_price: e.target.value }))}
                                                                    placeholder={selectedPosition.current_price?.toString() || 'Current price'}
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>

                                                        <Button
                                                            variant="dark"
                                                            className="test-button w-100"
                                                            onClick={handleClosePosition}
                                                            disabled={loading}
                                                        >
                                                            {loading ? <Spinner size="sm" /> : 'Close Position'}
                                                        </Button>
                                                    </div>
                                                </Tab>
                                            </Tabs>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* Info Section */}
                            <Alert variant="info" className="mt-3">
                                <strong>ℹ️ How to use:</strong>
                                <ol className="mb-0 mt-2">
                                    <li>Select a trading instance and create a test position with SL/TP prices</li>
                                    <li>Click on a position in the table to select it</li>
                                    <li>Use the tabs to test various trade management features</li>
                                    <li>Simulate price movements to see how SL/TP, trailing stops, breakeven, and drawdown exits work</li>
                                </ol>
                            </Alert>
                        </div>
                    </Card>
                </Container>
            </div>

            {/* Create Position Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaPlus className="me-2" />
                        Create Test Position
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Linked Algorithm *</Form.Label>
                                <Form.Select
                                    value={newPosition.linked_algo_id}
                                    onChange={(e) => setNewPosition((prev) => ({ ...prev, linked_algo_id: Number(e.target.value) }))}
                                    required
                                >
                                    <option value={0}>-- Select Algorithm --</option>
                                    {selectedInstance?.linked_algos?.map((algo) => (
                                        <option key={algo.id} value={algo.id}>
                                            {algo.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Symbol *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newPosition.symbol}
                                    onChange={(e) => setNewPosition((prev) => ({ ...prev, symbol: e.target.value.toUpperCase() }))}
                                    placeholder="e.g., AAPL"
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Direction</Form.Label>
                                <Form.Select
                                    value={newPosition.direction}
                                    onChange={(e) => setNewPosition((prev) => ({ ...prev, direction: e.target.value }))}
                                >
                                    <option value="LONG">LONG</option>
                                    <option value="SHORT">SHORT</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Entry Price *</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={newPosition.entry_price}
                                        onChange={(e) => setNewPosition((prev) => ({ ...prev, entry_price: e.target.value }))}
                                        required
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity *</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={newPosition.quantity}
                                    onChange={(e) => setNewPosition((prev) => ({ ...prev, quantity: e.target.value }))}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Stop Loss Price</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={newPosition.stop_loss_price}
                                        onChange={(e) => setNewPosition((prev) => ({ ...prev, stop_loss_price: e.target.value }))}
                                        placeholder="Optional"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Take Profit Price</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={newPosition.take_profit_price}
                                        onChange={(e) => setNewPosition((prev) => ({ ...prev, take_profit_price: e.target.value }))}
                                        placeholder="Optional"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleCreatePosition}
                        disabled={loading || !newPosition.linked_algo_id || !newPosition.symbol || !newPosition.entry_price || !newPosition.quantity}
                    >
                        {loading ? <Spinner size="sm" /> : 'Create Position'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Result Modal */}
            <Modal show={showResultModal} onHide={() => setShowResultModal(false)} size="lg" centered>
                <div className={result?.success ? 'result-header-success' : 'result-header-error'}
                    style={{
                        background: result?.success
                            ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                            : 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                        color: 'white',
                        padding: '1.5rem',
                        borderRadius: '0.5rem 0.5rem 0 0'
                    }}
                >
                    <h5 className="mb-0 d-flex align-items-center gap-2">
                        {result?.success ? <FaCheckCircle size={24} /> : <FaTimesCircle size={24} />}
                        {result?.success ? 'Test Successful' : 'Test Failed'}
                    </h5>
                </div>
                <Modal.Body>
                    {result && (
                        <>
                            <Alert variant={result.success ? 'success' : 'danger'}>
                                <strong>{result.message}</strong>
                            </Alert>

                            {result.error && (
                                <div className="mb-3">
                                    <h6>Error Details:</h6>
                                    <div className="result-box text-danger">{result.error}</div>
                                </div>
                            )}

                            {result.data && (
                                <div className="mb-3">
                                    <h6>Result Data:</h6>
                                    <div className="result-box">
                                        <pre className="mb-0">{JSON.stringify(result.data, null, 2)}</pre>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowResultModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Loading Overlay */}
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        background: '#11131a',
                        border: '1px solid #252a38',
                        padding: '2rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                    }}>
                        <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                        <h4 className="mt-3">Processing Test...</h4>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default TestTradeManagement;
